import { useState, useEffect, useCallback } from "react";
import {
  CalendarEvent,
  CalendarEventsType,
  DateTime,
  GeographicRegion,
  KLYDO_EVENT_PRIMARY_TYPE,
  KlydoPickMethod,
  State,
} from "../../../api/calendarEvent";
import { Klydo } from "../../../Types";
import klydoApiClient from "../../../api/klydo";
import {
  useUpsertKlydoEvent,
  useDeleteKlydoEvent,
} from "../hooks/useKlydoEvents";

import DSBox from "../../system-design/DSBox";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSSwitch from "../../system-design/DSSwitch";
import DSGrid from "../../system-design/DSGrid";
import DSAvatar from "../../system-design/DSAvatar";
import DSIconButton from "../../system-design/DSIconButton";
import DSTextField from "../../system-design/DSTextField";
import DSFormControl from "../../system-design/DSFormControl";
import DSInputLabel from "../../system-design/DSInputLabel";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSChip from "../../system-design/DSChip";
import { Close } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import KlydoLoopUrl from "../../Klydos/components/KlydoLoopUrl";
import KlydoModal from "../../Klydos/components/KlydoModal";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import KlydoPickMethodSelect from "./KlydoPickMethodSelect";
import CountryDropdown from "./CountryDropdown";
import StateDropdown from "./StateDropdown";
import StringChipEditor from "../../../components/Widgets/StringChipEditor";
import { areStatesEqual } from "../../../utils/util";

const ALL_PRIMARY_TYPES = [
  ...Object.values(CalendarEventsType),
  KLYDO_EVENT_PRIMARY_TYPE,
];

interface KlydoEventModalProps {
  open: boolean;
  onClose: () => void;
  event?: CalendarEvent;
  date?: Date;
  initialKlydoIds?: string[];
  minDate?: Date;
}

function KlydoEventModal({
  open,
  onClose,
  event,
  date,
  initialKlydoIds,
  minDate,
}: KlydoEventModalProps) {
  const getInitialDate = useCallback(() => {
    if (event) {
      return new Date(
        event.date.year || new Date().getFullYear(),
        event.date.month - 1,
        event.date.day,
      );
    }
    const initial = date || new Date();
    if (minDate && initial < minDate) return minDate;
    return initial;
  }, [event, date, minDate]);

  const [primaryType, setPrimaryType] = useState(
    event?.primaryType || KLYDO_EVENT_PRIMARY_TYPE,
  );
  const [name, setName] = useState(event?.name || "");
  const [description, setDescription] = useState(event?.description || "");
  const [selectedDate, setSelectedDate] = useState<Date>(getInitialDate());
  const [country, setCountry] = useState<GeographicRegion | null>(
    event?.country || null,
  );
  const [states, setStates] = useState<State[]>(event?.states || []);
  const [eventTypes, setEventTypes] = useState<string[]>(event?.type || []);
  const [ignoreEvent, setIgnoreEvent] = useState(event?.ignoreEvent ?? false);
  const [selectedKlydoIds, setSelectedKlydoIds] = useState<string[]>(
    event?.klydoIds || initialKlydoIds || [],
  );
  const [pickMethod, setPickMethod] = useState<KlydoPickMethod | "">(
    event?.pickMethod ?? "",
  );
  const [overrideOtherEvents, setOverrideOtherEvents] = useState(
    event?.overrideOtherEvents ?? false,
  );
  const [resolvedKlydos, setResolvedKlydos] = useState<Klydo[]>([]);
  const [isLoadingKlydos, setIsLoadingKlydos] = useState(false);
  const [showPickDialog, setShowPickDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedKlydo, setSelectedKlydo] = useState<Klydo | null>(null);

  const { mutateAsync: upsertEvent, isPending: isSaving } =
    useUpsertKlydoEvent();
  const { mutateAsync: deleteEvent, isPending: isDeleting } =
    useDeleteKlydoEvent();

  useEffect(() => {
    if (event) {
      setPrimaryType(event.primaryType || KLYDO_EVENT_PRIMARY_TYPE);
      setName(event.name || "");
      setDescription(event.description || "");
      setSelectedKlydoIds(event.klydoIds || []);
      setPickMethod(event.pickMethod ?? "");
      setOverrideOtherEvents(event.overrideOtherEvents ?? false);
      setCountry(event.country || null);
      setStates(event.states || []);
      setEventTypes(event.type || []);
      setIgnoreEvent(event.ignoreEvent ?? false);
      setSelectedDate(getInitialDate());
    } else if (date) {
      setSelectedDate(date);
      setSelectedKlydoIds(initialKlydoIds || []);
    }
  }, [event, date, initialKlydoIds, getInitialDate]);

  useEffect(() => {
    let isCurrent = true;
    if (selectedKlydoIds.length > 0) {
      setIsLoadingKlydos(true);
      klydoApiClient
        .getKlydosByIds(selectedKlydoIds)
        .then((klydos) => {
          if (isCurrent) setResolvedKlydos(klydos);
        })
        .catch(() => {
          if (isCurrent) setResolvedKlydos([]);
        })
        .finally(() => {
          if (isCurrent) setIsLoadingKlydos(false);
        });
    } else {
      setResolvedKlydos([]);
    }
    return () => {
      isCurrent = false;
    };
  }, [selectedKlydoIds]);

  const getEventDate = (): DateTime => ({
    year: selectedDate.getFullYear(),
    month: selectedDate.getMonth() + 1,
    day: selectedDate.getDate(),
  });

  const handleSave = async () => {
    const eventDate = getEventDate();
    const eventId =
      event?.id ||
      `daily-klydo-${eventDate.year}-${String(eventDate.month).padStart(2, "0")}-${String(eventDate.day).padStart(2, "0")}`;
    await upsertEvent({
      eventId,
      event: {
        name,
        description: description || undefined,
        primaryType,
        type: eventTypes.length > 0 ? eventTypes : [primaryType],
        country: country || undefined,
        states,
        date: eventDate,
        klydoIds: selectedKlydoIds,
        pickMethod: pickMethod || undefined,
        overrideOtherEvents,
        ignoreEvent: ignoreEvent || undefined,
      },
    });
    onClose();
  };

  const handleDelete = async () => {
    if (event?.id) {
      await deleteEvent(event.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  const handleStateSelect = (state: State | null) => {
    if (state && !states.some((s) => areStatesEqual(s, state))) {
      setStates((prev) => [...prev, state]);
    }
  };

  const dateInputValue = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;

  const minDateValue = minDate
    ? `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, "0")}-${String(minDate.getDate()).padStart(2, "0")}`
    : undefined;

  return (
    <>
      <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DSDialogTitle>
          {event ? "Edit Klydo Event" : "Create Klydo Event"}
        </DSDialogTitle>
        <DSDialogContent dividers>
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
          >
            <DSFormControl fullWidth size="small">
              <DSInputLabel>Event Type</DSInputLabel>
              <DSSelect
                value={primaryType}
                label="Event Type"
                onChange={(changeEvent) =>
                  setPrimaryType(changeEvent.target.value as string)
                }
              >
                {ALL_PRIMARY_TYPES.map((t) => (
                  <DSMenuItem key={t} value={t}>
                    {t}
                  </DSMenuItem>
                ))}
              </DSSelect>
            </DSFormControl>

            <DSTextField
              label="Event Name"
              value={name}
              onChange={(changeEvent) => setName(changeEvent.target.value)}
              fullWidth
              size="small"
            />

            <DSTextField
              label="Description"
              value={description}
              onChange={(changeEvent) =>
                setDescription(changeEvent.target.value)
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />

            <DSTextField
              label="Date"
              type="date"
              value={dateInputValue}
              onChange={(changeEvent) => {
                const [y, m, d] = changeEvent.target.value
                  .split("-")
                  .map(Number);
                if (y && m && d) {
                  const chosen = new Date(y, m - 1, d);
                  if (minDate && chosen < minDate) return;
                  setSelectedDate(chosen);
                }
              }}
              inputProps={{ min: minDateValue }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <CountryDropdown initialCountry={country} onSelect={setCountry} />

            <DSBox>
              <StateDropdown
                country={country}
                onSelect={handleStateSelect}
                disabled={!country}
              />
              <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {states.map((state) => (
                  <DSChip
                    key={state.iso}
                    label={`${state.name} (${state.iso})`}
                    onDelete={() =>
                      setStates((prev) =>
                        prev.filter((s) => !areStatesEqual(s, state)),
                      )
                    }
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                  />
                ))}
              </DSBox>
            </DSBox>

            <StringChipEditor
              label="Add Type"
              values={eventTypes}
              onChange={setEventTypes}
              placeholder="Enter event type"
              helperText="Add event types one at a time"
            />

            <DSBox>
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <DSTypography variant="body2">
                  Klydos ({selectedKlydoIds.length})
                </DSTypography>
                <DSPrimaryButton
                  variant="outlined"
                  size="small"
                  onClick={() => setShowPickDialog(true)}
                >
                  Pick Klydos
                </DSPrimaryButton>
              </DSBox>

              {isLoadingKlydos && <DSCircularProgress size={20} />}

              {resolvedKlydos.length > 0 && (
                <DSGrid container spacing={1}>
                  {resolvedKlydos.map((klydo) => (
                    <DSGrid item xs={4} key={klydo.id}>
                      <DSBox
                        onClick={() => setSelectedKlydo(klydo)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          p: 1,
                          position: "relative",
                          cursor: "pointer",
                        }}
                      >
                        <DSIconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedKlydoIds((prev) =>
                              prev.filter((klydoId) => klydoId !== klydo.id),
                            );
                          }}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            p: 0.25,
                          }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </DSIconButton>
                        <DSBox
                          sx={{
                            borderRadius: "50%",
                            width: 48,
                            height: 48,
                            overflow: "hidden",
                          }}
                        >
                          {klydo.loopUrl ? (
                            <KlydoLoopUrl loopUrl={klydo.loopUrl} />
                          ) : (
                            <DSAvatar sx={{ width: "100%", height: "100%" }}>
                              {klydo.name?.charAt(0) || "K"}
                            </DSAvatar>
                          )}
                        </DSBox>
                        <DSTypography
                          variant="caption"
                          sx={{ mt: 0.5, textAlign: "center" }}
                        >
                          {klydo.name}
                        </DSTypography>
                      </DSBox>
                    </DSGrid>
                  ))}
                </DSGrid>
              )}
            </DSBox>

            <KlydoPickMethodSelect
              value={pickMethod}
              onChange={setPickMethod}
            />

            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSTypography>Override other events</DSTypography>
              <DSSwitch
                checked={overrideOtherEvents}
                onChange={() => setOverrideOtherEvents((prev) => !prev)}
              />
            </DSBox>

            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSTypography>Ignore Event</DSTypography>
              <DSSwitch
                checked={ignoreEvent}
                onChange={() => setIgnoreEvent((prev) => !prev)}
              />
            </DSBox>
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          {event && (
            <DSSecondaryButton
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting || isSaving}
              sx={{ color: "error.main", mr: "auto" }}
            >
              Delete
            </DSSecondaryButton>
          )}
          <DSSecondaryButton
            onClick={onClose}
            disabled={isSaving || isDeleting}
          >
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={handleSave}
            disabled={isSaving || isDeleting || !pickMethod}
          >
            {isSaving ? <DSCircularProgress size={20} /> : "Save"}
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>

      {showPickDialog && (
        <KlydosPickDialog
          onClose={() => setShowPickDialog(false)}
          selectedKlydos={selectedKlydoIds}
          onPickKlydos={setSelectedKlydoIds}
        />
      )}

      {selectedKlydo && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={true}
          onClose={() => setSelectedKlydo(null)}
        >
          <KlydoModal
            klydoId={selectedKlydo.id}
            setShowModal={() => setSelectedKlydo(null)}
          />
        </DSDialog>
      )}

      <DSDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>Delete Klydo Event</DSDialogTitle>
        <DSDialogContent>
          <DSTypography>
            Are you sure you want to delete this klydo event? This action cannot
            be undone.
          </DSTypography>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={handleDelete}
            disabled={isDeleting}
            sx={{ color: "error.main" }}
          >
            {isDeleting ? <DSCircularProgress size={20} /> : "Delete"}
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
    </>
  );
}

export default KlydoEventModal;
